// src/components/entities/EntityForm.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  createEntity,
  updateEntity,
  fetchEntity,
  clearEntityError,
  clearEntitySuccess,
} from '../../store/actions/entityActions';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';
import ConfirmDialog from '../common/ConfirmDialog';

const EntityForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { selectedEntity, loading, error, success } = useSelector(
    (state) => state.entities
  );

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: '',
    color: '#2563eb',
    image: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    email: '',
    website: '',
    lat: '',
    long: '',
    isOpen: true,
    order: 0,
  });

  const [errors, setErrors] = useState({});
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditMode = !!id;

  // Styles object
  const styles = {
    container: {
      maxWidth: '1024px',
      margin: '0 auto',
      padding: '2rem 1rem',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem',
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: '#111827',
      margin: 0,
    },
    cancelButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#ffffff',
      color: '#374151',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    cancelButtonHover: {
      backgroundColor: '#f9fafb',
    },
    form: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      padding: '2rem',
    },
    section: {
      marginBottom: '2rem',
      paddingBottom: '2rem',
      borderBottom: '1px solid #e5e7eb',
    },
    sectionTitle: {
      fontSize: '1.125rem',
      fontWeight: 600,
      color: '#111827',
      marginBottom: '1.5rem',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '1.5rem',
    },
    fullWidth: {
      gridColumn: 'span 2',
    },
    fieldGroup: {
      marginBottom: '1rem',
    },
    label: {
      display: 'block',
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#374151',
      marginBottom: '0.5rem',
    },
    input: {
      width: '100%',
      padding: '0.625rem',
      fontSize: '0.875rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
      transition: 'border-color 0.2s, boxShadow 0.2s',
    },
    inputFocus: {
      borderColor: '#2563eb',
      boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.1)',
    },
    textarea: {
      width: '100%',
      padding: '0.625rem',
      fontSize: '0.875rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
      minHeight: '100px',
      resize: 'vertical',
    },
    inputError: {
      borderColor: '#dc2626',
    },
    errorText: {
      marginTop: '0.25rem',
      fontSize: '0.75rem',
      color: '#dc2626',
    },
    helperText: {
      marginTop: '0.25rem',
      fontSize: '0.75rem',
      color: '#6b7280',
    },
    imageUpload: {
      marginTop: '0.5rem',
    },
    imagePreview: {
      width: '200px',
      height: '200px',
      borderRadius: '0.5rem',
      objectFit: 'cover',
      backgroundColor: '#f3f4f6',
      border: '1px solid #e5e7eb',
      marginBottom: '1rem',
    },
    imageUploadButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#ffffff',
      color: '#374151',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'all 0.2s',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    colorPicker: {
      width: '60px',
      height: '40px',
      padding: '0',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      cursor: 'pointer',
    },
    checkbox: {
      width: '1rem',
      height: '1rem',
      marginRight: '0.5rem',
      cursor: 'pointer',
    },
    checkboxLabel: {
      display: 'flex',
      alignItems: 'center',
      fontSize: '0.875rem',
      color: '#374151',
      cursor: 'pointer',
    },
    buttonGroup: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '1rem',
      marginTop: '2rem',
    },
    submitButton: {
      padding: '0.75rem 1.5rem',
      backgroundColor: '#2563eb',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
    submitButtonHover: {
      backgroundColor: '#1d4ed8',
    },
    submitButtonDisabled: {
      backgroundColor: '#9ca3af',
      cursor: 'not-allowed',
    },
  };

  useEffect(() => {
    if (isEditMode && id) {
      dispatch(fetchEntity(id));
    }
  }, [dispatch, id, isEditMode]);

  useEffect(() => {
    if (isEditMode && selectedEntity) {
      setFormData({
        name: selectedEntity.name || '',
        description: selectedEntity.description || '',
        icon: selectedEntity.icon || '',
        color: selectedEntity.color || '#2563eb',
        image: selectedEntity.image || '',
        address: selectedEntity.address || '',
        city: selectedEntity.city || '',
        state: selectedEntity.state || '',
        zipCode: selectedEntity.zipCode || '',
        phone: selectedEntity.phone || '',
        email: selectedEntity.email || '',
        website: selectedEntity.website || '',
        lat: selectedEntity.lat || '',
        long: selectedEntity.long || '',
        isOpen: selectedEntity.isOpen ?? true,
        order: selectedEntity.order || 0,
      });
      setImagePreview(selectedEntity.image);
    }
  }, [selectedEntity, isEditMode]);

  // Cleanup
  useEffect(() => {
    return () => {
      dispatch(clearEntityError());
      dispatch(clearEntitySuccess());
    };
  }, [dispatch]);

  // Memoized validation function
  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Le nom est requis';
    }

    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Format d\'email invalide';
    }

    if (formData.phone && !/^[0-9+\-\s()]+$/.test(formData.phone)) {
      newErrors.phone = 'Format de téléphone invalide';
    }

    if (formData.website && !/^https?:\/\/.+/.test(formData.website)) {
      newErrors.website = 'URL invalide (doit commencer par http:// ou https://)';
    }

    if (formData.lat && (formData.lat < -90 || formData.lat > 90)) {
      newErrors.lat = 'Latitude doit être entre -90 et 90';
    }

    if (formData.long && (formData.long < -180 || formData.long > 180)) {
      newErrors.long = 'Longitude doit être entre -180 et 180';
    }

    return newErrors;
  }, [formData.name, formData.email, formData.phone, formData.website, formData.lat, formData.long]);

  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    // Clear error for this field
    setErrors((prev) => ({
      ...prev,
      [name]: undefined,
    }));
  }, []);

  const handleImageUpload = useCallback((e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setFormData((prev) => ({
          ...prev,
          image: reader.result,
        }));
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    setIsSubmitting(true);

    try {
      if (isEditMode) {
        await dispatch(updateEntity(id, formData));
      } else {
        await dispatch(createEntity(formData));
      }
      navigate('/admin/entities');
    } catch (error) {
      console.error('Form submission failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [dispatch, formData, id, isEditMode, navigate, validateForm]);

  const handleCancel = useCallback(() => {
    if (Object.keys(formData).some(key => formData[key] !== '' && formData[key] !== false && formData[key] !== 0)) {
      setShowCancelConfirm(true);
    } else {
      navigate('/admin/entities');
    }
  }, [formData, navigate]);

  if (loading && isEditMode) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>
          {isEditMode ? 'Modifier l\'entité' : 'Créer une nouvelle entité'}
        </h1>
        <button
          onClick={handleCancel}
          style={styles.cancelButton}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = styles.cancelButtonHover.backgroundColor;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = styles.cancelButton.backgroundColor;
          }}
        >
          Annuler
        </button>
      </div>

      {error && (
        <Alert
          type="error"
          message={error}
          onClose={() => dispatch(clearEntityError())}
        />
      )}

      {success && (
        <Alert
          type="success"
          message={success}
          onClose={() => dispatch(clearEntitySuccess())}
        />
      )}

      <form onSubmit={handleSubmit} style={styles.form}>
        {/* Basic Information */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Informations de base</h2>
          <div style={styles.grid}>
            <div style={styles.fullWidth}>
              <div style={styles.fieldGroup}>
                <label style={styles.label} htmlFor="name">
                  Nom *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  style={{
                    ...styles.input,
                    ...(errors.name ? styles.inputError : {}),
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = styles.inputFocus.borderColor;
                    e.target.style.boxShadow = styles.inputFocus.boxShadow;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = styles.input.borderColor;
                    e.target.style.boxShadow = 'none';
                  }}
                />
                {errors.name && (
                  <div style={styles.errorText}>{errors.name}</div>
                )}
              </div>
            </div>

            <div style={styles.fullWidth}>
              <div style={styles.fieldGroup}>
                <label style={styles.label} htmlFor="description">
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  style={styles.textarea}
                  rows="4"
                />
              </div>
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label} htmlFor="icon">
                Icône (emoji)
              </label>
              <input
                type="text"
                id="icon"
                name="icon"
                value={formData.icon}
                onChange={handleChange}
                style={styles.input}
                placeholder="ex: 🏢, 🏥, 🏪"
              />
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label} htmlFor="color">
                Couleur
              </label>
              <input
                type="color"
                id="color"
                name="color"
                value={formData.color}
                onChange={handleChange}
                style={styles.colorPicker}
              />
            </div>
          </div>
        </div>

        {/* Location Information */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Localisation</h2>
          <div style={styles.grid}>
            <div style={styles.fullWidth}>
              <div style={styles.fieldGroup}>
                <label style={styles.label} htmlFor="address">
                  Adresse
                </label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  style={styles.input}
                />
              </div>
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label} htmlFor="city">
                Ville
              </label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                style={styles.input}
              />
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label} htmlFor="state">
                État / Région
              </label>
              <input
                type="text"
                id="state"
                name="state"
                value={formData.state}
                onChange={handleChange}
                style={styles.input}
              />
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label} htmlFor="zipCode">
                Code postal
              </label>
              <input
                type="text"
                id="zipCode"
                name="zipCode"
                value={formData.zipCode}
                onChange={handleChange}
                style={styles.input}
              />
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Contact</h2>
          <div style={styles.grid}>
            <div style={styles.fieldGroup}>
              <label style={styles.label} htmlFor="phone">
                Téléphone
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                style={{
                  ...styles.input,
                  ...(errors.phone ? styles.inputError : {}),
                }}
              />
              {errors.phone && (
                <div style={styles.errorText}>{errors.phone}</div>
              )}
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label} htmlFor="email">
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                style={{
                  ...styles.input,
                  ...(errors.email ? styles.inputError : {}),
                }}
              />
              {errors.email && (
                <div style={styles.errorText}>{errors.email}</div>
              )}
            </div>

            <div style={styles.fullWidth}>
              <div style={styles.fieldGroup}>
                <label style={styles.label} htmlFor="website">
                  Site web
                </label>
                <input
                  type="url"
                  id="website"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  style={{
                    ...styles.input,
                    ...(errors.website ? styles.inputError : {}),
                  }}
                  placeholder="https://example.com"
                />
                {errors.website && (
                  <div style={styles.errorText}>{errors.website}</div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Coordinates */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Coordonnées GPS</h2>
          <div style={styles.grid}>
            <div style={styles.fieldGroup}>
              <label style={styles.label} htmlFor="lat">
                Latitude
              </label>
              <input
                type="number"
                id="lat"
                name="lat"
                value={formData.lat}
                onChange={handleChange}
                step="any"
                style={{
                  ...styles.input,
                  ...(errors.lat ? styles.inputError : {}),
                }}
              />
              {errors.lat && (
                <div style={styles.errorText}>{errors.lat}</div>
              )}
              <div style={styles.helperText}>
                Entre -90 et 90
              </div>
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label} htmlFor="long">
                Longitude
              </label>
              <input
                type="number"
                id="long"
                name="long"
                value={formData.long}
                onChange={handleChange}
                step="any"
                style={{
                  ...styles.input,
                  ...(errors.long ? styles.inputError : {}),
                }}
              />
              {errors.long && (
                <div style={styles.errorText}>{errors.long}</div>
              )}
              <div style={styles.helperText}>
                Entre -180 et 180
              </div>
            </div>
          </div>
        </div>

        {/* Image */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Image</h2>
          <div>
            {imagePreview && (
              <img
                src={imagePreview}
                alt="Preview"
                style={styles.imagePreview}
              />
            )}
            <div style={styles.imageUpload}>
              <input
                type="file"
                id="image"
                accept="image/*"
                onChange={handleImageUpload}
                style={{ display: 'none' }}
              />
              <button
                type="button"
                onClick={() => document.getElementById('image').click()}
                style={styles.imageUploadButton}
              >
                <svg style={{ width: '1.25rem', height: '1.25rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {imagePreview ? 'Changer l\'image' : 'Télécharger une image'}
              </button>
            </div>
          </div>
        </div>

        {/* Settings */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Paramètres</h2>
          <div style={styles.grid}>
            <div style={styles.fieldGroup}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="isOpen"
                  checked={formData.isOpen}
                  onChange={handleChange}
                  style={styles.checkbox}
                />
                Ouvert actuellement
              </label>
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label} htmlFor="order">
                Ordre d'affichage
              </label>
              <input
                type="number"
                id="order"
                name="order"
                value={formData.order}
                onChange={handleChange}
                style={styles.input}
                min="0"
              />
            </div>
          </div>
        </div>

        <div style={styles.buttonGroup}>
          <button
            type="button"
            onClick={handleCancel}
            style={styles.cancelButton}
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              ...styles.submitButton,
              ...(isSubmitting ? styles.submitButtonDisabled : {}),
            }}
            onMouseEnter={(e) => {
              if (!isSubmitting) {
                e.currentTarget.style.backgroundColor = styles.submitButtonHover.backgroundColor;
              }
            }}
            onMouseLeave={(e) => {
              if (!isSubmitting) {
                e.currentTarget.style.backgroundColor = styles.submitButton.backgroundColor;
              }
            }}
          >
            {isSubmitting 
              ? (isEditMode ? 'Modification...' : 'Création...') 
              : (isEditMode ? 'Modifier' : 'Créer')}
          </button>
        </div>
      </form>

      <ConfirmDialog
        isOpen={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        onConfirm={() => navigate('/admin/entities')}
        title="Annuler les modifications"
        message="Vous avez des modifications non enregistrées. Êtes-vous sûr de vouloir quitter ?"
        confirmText="Quitter"
        cancelText="Continuer l'édition"
        type="warning"
      />
    </div>
  );
};

export default EntityForm;